import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useEffect, useRef, useState } from 'react';

export default function TintingServiceSection() {
  const videoRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Lazy load: only inject iframe src when container enters viewport
    const lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !videoSrc) {
            setVideoSrc('https://player.vimeo.com/video/1164580562?autoplay=1&loop=1&muted=1&background=1&controls=0');
          }
        });
      },
      { rootMargin: '200px', threshold: 0 }
    );

    lazyObserver.observe(container);
    return () => lazyObserver.disconnect();
  }, [videoSrc]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoSrc) return;

    // Audio control: unmute when 50% visible
    const audioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 1 }), '*');
          } else {
            videoElement.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
          }
        });
      },
      { threshold: 0.5 }
    );

    audioObserver.observe(videoElement);
    return () => audioObserver.disconnect();
  }, [videoSrc]);

  const whatsappMessage = encodeURIComponent('Hola, me gustaría consultar sobre Laminados (Window Tint & Protection)');
  const whatsappLink = `https://wa.me/18096318952?text=${whatsappMessage}`;

  const benefits = [
    {
      id: 'tint_heat',
      icon: 'FireIcon',
      title: 'RECHAZO DE CALOR 96%',
      description: 'Reducción térmica extrema para máximo confort'
    },
    {
      id: 'tint_uv',
      icon: 'SunIcon',
      title: '100% PROTECCIÓN UV',
      description: 'Bloqueo total de rayos ultravioleta dañinos'
    },
    {
      id: 'tint_vision',
      icon: 'EyeIcon',
      title: 'VISIÓN FULL HD',
      description: 'Claridad óptica superior sin distorsión'
    },
    {
      id: 'tint_privacy',
      icon: 'ShieldCheckIcon',
      title: 'ALTA PRIVACIDAD',
      description: 'Control solar inteligente y seguridad'
    }
  ];

  const goldenLine = [
    {
      id: 'golden_black',
      name: 'Golden Black',
      heatRejection: '96%',
      uvProtection: '100%',
      warranty: '12 años',
      features: [
        'Rechazo de calor 96%',
        'Protección UV 100%',
        'Visión Full HD',
        'Tecnología cerámica avanzada',
        'Garantía 12 años'
      ],
      highlight: true
    },
    {
      id: 'golden_premium',
      name: 'Golden Premium',
      subtitle: 'Carbón Ceramic',
      heatRejection: '95%',
      uvProtection: '99%',
      warranty: '10 años',
      features: [
        'Rechazo de calor 95%',
        'Alta durabilidad',
        'Estética premium',
        'Tecnología carbón cerámico',
        'Garantía 10 años'
      ]
    },
    {
      id: 'golden_silver',
      name: 'Golden Silver',
      heatRejection: '90%',
      uvProtection: '99%',
      warranty: '10 años',
      features: [
        'Tonalidad espejada',
        'Alta privacidad',
        'Visión nítida',
        'Acabado reflectante',
        'Garantía 10 años'
      ]
    }
  ];

  const windshieldProtection = [
    {
      id: 'clearplex',
      name: 'ClearPlex',
      logo: '/assets/images/clearplex_logo.png',
      description: 'Escudo exterior contra impactos de piedras',
      features: ['99.9% bloqueo UV', 'Protección contra impactos', 'Claridad total']
    },
    {
      id: 'protection_pro',
      name: 'ProtectionPro',
      logo: '/assets/images/suntek_logo.png',
      description: 'Film de seguridad para parabrisas',
      features: ['Resistencia a impactos', 'Bloqueo UV 99.9%', 'Instalación profesional']
    },
    {
      id: 'suntek',
      name: 'SunTek Window Films',
      logo: '/assets/images/suntek_logo.png',
      description: 'Tecnología Made in USA',
      features: ['Confort térmico máximo', 'Durabilidad superior', 'Garantía del fabricante']
    }
  ];

  return (
    <section className="py-32 px-6 lg:px-12 bg-black" id="tintado">
      <div className="max-w-[1600px] mx-auto">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <span className="text-primary font-heading text-sm tracking-[0.3em] mb-4 block">
              03 / LAMINADOS (WINDOW TINT & PROTECTION)
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-white mb-6 tracking-tight">
              CONTROL SOLAR
              <br />
              <span className="text-gradient-red">Y SEGURIDAD DE CRISTALES</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Reducción térmica y privacidad con tecnología inteligente de última generación. Línea profesional Golden para máximo rendimiento.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-primary font-heading text-sm tracking-wider">
                  96% RECHAZO DE CALOR
                </span>
              </div>
              <div className="glass-card px-6 py-3 rounded-full">
                <span className="text-white/80 font-heading text-sm tracking-wider">
                  100% PROTECCIÓN UV
                </span>
              </div>
            </div>
          </div>

          <div className="relative flex justify-center">
            <div className="w-full max-w-md" ref={containerRef}>
              {/* Mobile: 16:9, Desktop: 9:16 — fixed aspect ratio prevents layout shift */}
              <div className="aspect-[16/9] md:aspect-[9/16] rounded-[32px] overflow-hidden relative shadow-[0_20px_60px_rgba(220,38,38,0.3),0_0_80px_rgba(220,38,38,0.15)] border-2 border-primary/40 hover:border-primary/60 hover:shadow-[0_25px_70px_rgba(220,38,38,0.4),0_0_100px_rgba(220,38,38,0.2)] transition-all duration-500 hover:scale-[1.02] bg-gradient-to-br from-primary/5 to-transparent">
                {/* Poster placeholder shown until video loads */}
                {!isLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-black via-zinc-900 to-black">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center mx-auto mb-3 animate-pulse">
                        <svg className="w-7 h-7 text-primary" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <p className="text-white/40 text-xs font-heading tracking-wider">LAMINADOS</p>
                    </div>
                  </div>
                )}
                {videoSrc && (
                  <iframe
                    ref={videoRef}
                    src={videoSrc}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Laminados de Cristales - Control Solar y Seguridad"
                    onLoad={() => setIsLoaded(true)}
                  />
                )}
              </div>
            </div>
            <div className="absolute -bottom-8 -right-8 glass-card p-8 rounded-[32px] max-w-xs">
              <div className="text-primary font-heading text-4xl mb-2">12</div>
              <div className="text-white/80 text-sm font-medium">
                Años de garantía Golden Black
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit) => (
            <div
              key={benefit.id}
              className="glass-card p-6 rounded-[24px] hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                <Icon
                  name={benefit.icon as any}
                  size={24}
                  variant="outline"
                  className="text-primary"
                />
              </div>
              <h3 className="font-heading text-white text-sm tracking-wider mb-2">
                {benefit.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Golden Line Professional */}
        <div className="mb-20">
          <div className="flex flex-col items-center mb-12">
            <div className="w-32 h-32 bg-white/5 rounded-2xl flex items-center justify-center p-4 mb-6">
              <AppImage
                src="/assets/images/golden_tint_logo.png"
                alt="Golden Window Tint logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-white text-center tracking-wider">
              LÍNEA PROFESIONAL GOLDEN
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {goldenLine.map((product) => (
              <div
                key={product.id}
                className={`glass-card p-8 rounded-[32px] hover:bg-white/10 transition-all duration-300 relative ${
                  product.highlight ? 'border-2 border-primary mt-6' : ''
                }`}
              >
                {product.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary px-4 py-1 rounded-full whitespace-nowrap">
                    <span className="text-white font-heading text-[10px] tracking-wider">
                      MÁXIMO RENDIMIENTO
                    </span>
                  </div>
                )}
                <h4 className="font-heading text-xl text-white mb-1 tracking-wider">
                  {product.name}
                </h4>
                {product.subtitle && (
                  <p className="text-primary text-xs font-medium mb-4">{product.subtitle}</p>
                )}
                <div className="flex gap-4 mb-6">
                  <div className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/60 text-center">
                    <div className="text-primary text-[9px] font-medium leading-tight">Rechazo Calor</div>
                    <div className="text-white font-heading text-sm leading-tight">{product.heatRejection}</div>
                  </div>
                  <div className="flex flex-col items-center justify-center w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/60 text-center">
                    <div className="text-primary text-[9px] font-medium leading-tight">UV Block</div>
                    <div className="text-white font-heading text-sm leading-tight">{product.uvProtection}</div>
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  {product.features.map((feature, idx) => (
                    <li
                      key={`${product.id}_feature_${idx}`}
                      className="flex items-start gap-2 text-white/70 text-sm"
                    >
                      <Icon
                        name="CheckIcon"
                        size={16}
                        variant="solid"
                        className="text-primary mt-0.5 flex-shrink-0"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-center pt-4 border-t border-white/10">
                  <span className="text-white/60 text-xs">Garantía: </span>
                  <span className="text-primary font-heading text-sm">{product.warranty}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Windshield Protection */}
        <div className="mb-12">
          <h3 className="font-heading text-2xl md:text-3xl text-white text-center mb-12 tracking-wider">
            PROTECCIÓN DE PARABRISAS
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {windshieldProtection.map((product) => (
              <div
                key={product.id}
                className="glass-card p-6 rounded-[24px] hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center p-2">
                    <AppImage
                      src={product.logo}
                      alt={`${product.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h4 className="font-heading text-lg text-white tracking-wider">
                    {product.name}
                  </h4>
                </div>
                <p className="text-white/60 text-sm mb-4">{product.description}</p>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li
                      key={`${product.id}_feature_${idx}`}
                      className="flex items-start gap-2 text-white/70 text-xs"
                    >
                      <Icon
                        name="CheckIcon"
                        size={14}
                        variant="solid"
                        className="text-primary mt-0.5 flex-shrink-0"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-primary hover:bg-primary/90 text-white px-12 py-5 rounded-full font-heading text-sm tracking-wider transition-all duration-300 hover:scale-105"
          >
            CONSULTAR AHORA
          </a>
          <p className="text-white/50 text-xs mt-4">Cotización personalizada vía WhatsApp</p>
        </div>
      </div>
    </section>
  );
}